import { FunctionComponent, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { useAppSelector } from "@/redux/hooks";
import { listSelector } from "@/pages/Private/redux/list/list.slice";
import { classNames } from "@/utils/classNames";

import { PromptEditor } from "./PromptEditor";

interface SnippetPromptPaneProps {
	name: string;
	prompt: string;
	nameError?: string;
	editorKey: string;
	autoFocusName?: boolean;
	onChangeName: (name: string) => void;
	onChangePrompt: (prompt: string) => void;
}

export const SnippetPromptPane: FunctionComponent<SnippetPromptPaneProps> = ({
	name,
	prompt,
	nameError,
	editorKey,
	autoFocusName = false,
	onChangeName,
	onChangePrompt,
}) => {
	const { t } = useTranslation();
	const { currentLeadIndex } = useAppSelector(listSelector);
	const nameInputRef = useRef<HTMLInputElement>(null);

	// Focus the name field when entering "new" mode (modal opened for a new
	// snippet, or "Create new" clicked while editing) so typing starts there.
	useEffect(() => {
		if (autoFocusName) {
			nameInputRef.current?.focus();
		}
	}, [autoFocusName]);

	return (
		<div className="flex-1 min-w-0 border-r border-border-light flex flex-col overflow-hidden">
			<div className="flex-1 overflow-y-auto px-6 py-5 min-w-0">
				<div className="flex flex-col gap-1.5 mb-4">
					<div className="flex items-center justify-between">
						<label className="text-[12.5px] font-semibold text-text">
							{t("aiSnippet.modal.name.label")}
						</label>
						<span className="text-[11.5px] text-text-meta font-normal">
							{t("aiSnippet.modal.name.helper")}
						</span>
					</div>
					<div
						className={classNames(
							"flex items-stretch border rounded-lg overflow-hidden transition-all bg-white",
							nameError
								? "border-status-error focus-within:shadow-[0_0_0_3px_rgba(223,40,0,0.08)]"
								: "border-border-light focus-within:border-blue-action focus-within:shadow-[0_0_0_3px_rgba(29,61,242,0.08)]"
						)}
					>
						<span className="bg-background-light text-text-meta px-2.5 inline-flex items-center font-semibold text-base">
							{"{"}
						</span>
						<input
							ref={nameInputRef}
							className="flex-1 border-0 outline-none px-2.5 py-2 text-[13px] bg-white text-text"
							placeholder={t("aiSnippet.modal.name.placeholder")}
							value={name}
							onChange={(e) => onChangeName(e.target.value)}
						/>
						<span className="bg-background-light text-text-meta px-2.5 inline-flex items-center font-semibold text-base">
							{"}"}
						</span>
					</div>
					{nameError && <div className="text-xs text-status-error mt-0.5">{nameError}</div>}
				</div>

				<div className="flex flex-col gap-1.5 mb-4">
					<div className="flex items-center justify-between">
						<label className="text-[12.5px] font-semibold text-text">
							{t("aiSnippet.modal.prompt.label")}
						</label>
						<span className="text-[11.5px] text-text-meta font-normal">
							{t("aiSnippet.modal.prompt.helper")}
						</span>
					</div>
					<PromptEditor
						key={editorKey}
						currentLeadIndex={currentLeadIndex}
						handleChange={onChangePrompt}
						value={prompt}
					/>
				</div>
			</div>
		</div>
	);
};
